import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { S, Icon, BtnPrimary, BtnSecondary, formatCHF } from '../regureStyles';
import useRcProOfferFunctions, { PERCENT_FULL_TIME } from './useRcProOfferFunctions';
import ClientSelector from './ClientSelector';
import BrokerSelect from './BrokerSelect';
import PaymentFrequencyField from './PaymentFrequencyField';

// Canton codes are stable; labels come from i18n.
const CANTON_CODES = ['AG','AI','AR','BE','BL','BS','FR','GE','GL','GR','JU','LU','NE','NW','OW','SG','SH','SO','SZ','TG','TI','UR','VD','VS','ZG','ZH'];

const inputStyle = {
  width: '100%',
  padding: '9px 12px',
  background: '#fff',
  border: `1px solid ${S.borderLight}`,
  borderRadius: 8,
  fontSize: 13,
  color: S.textPrimary,
  outline: 'none',
  boxSizing: 'border-box',
};
// The engine echoes the decimal FTE it rated; the summary shows the same
// percentage the operator typed. 0.3 -> "30%", 0.335 -> "33.5%".
const fmtActivityPct = (fte) => {
  const n = Number(fte);
  if (!Number.isFinite(n)) return '—';
  return `${Math.round(n * PERCENT_FULL_TIME * 100) / 100}%`;
};

const labelStyle = { fontSize: 11, fontWeight: 600, color: S.textSecondary, display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.5 };

const SectionTitle = ({ children }) => (
  <div style={{ fontSize: 12, fontWeight: 700, color: S.burgundy, textTransform: 'uppercase', letterSpacing: 0.8, margin: '18px 0 10px' }}>
    {children}
  </div>
);

const Field = ({ label, children, span = 1 }) => (
  <div style={{ gridColumn: `span ${span}`, marginBottom: 10 }}>
    <label style={labelStyle}>{label}</label>
    {children}
  </div>
);

// Activity (work percentage) — ONE component, used by every personnel row
// shape. It was previously two separate inputs and only the assistant one
// got the D-16 clamp, so an ordinary doctor row still accepted "30" as an
// FTE and the two shapes disagreed about the same field. Sharing the input
// makes that class of drift impossible rather than merely fixed.
//
// The unit is a PERCENTAGE, 1-100, because that is what the value means to
// the person entering it. The engine's decimal FTE is derived at submit.
const ActivityPercentField = ({ row, onChange, t }) => (
  <Field label={t('offers.rcpro.activityPercent')}>
    <div style={{ position: 'relative' }}>
      <input
        type='number' step='1' min='1' max={PERCENT_FULL_TIME}
        value={row.activityPercent}
        onChange={e => {
          // Clamp on keystroke as well as on the attribute so a paste and a
          // typed-past-the-max both bounce back instead of reaching the
          // server. An empty string is allowed through so the field can be
          // cleared while editing; `missingFields` blocks submit.
          const raw = e.target.value;
          if (raw === '') return onChange(row.id, 'activityPercent', raw);
          const n = Number(raw);
          if (!Number.isFinite(n)) return onChange(row.id, 'activityPercent', raw);
          if (n > PERCENT_FULL_TIME) return onChange(row.id, 'activityPercent', String(PERCENT_FULL_TIME));
          if (n < 0) return onChange(row.id, 'activityPercent', '0');
          onChange(row.id, 'activityPercent', raw);
        }}
        style={{ ...inputStyle, paddingRight: 26 }}
      />
      <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: S.textMuted, pointerEvents: 'none' }}>%</span>
    </div>
    <div style={{ fontSize: 11, color: S.textMuted, marginTop: 4 }}>
      {t('offers.rcpro.activityPercentHint')}
    </div>
  </Field>
);

// PersonnelRow supports three shapes: ordinary specialty, CUSTOM,
// and Médecin assistant (references another personnel row's premium).
const PersonnelRow = ({ row, idx, options, personnel, isOnlyRow, onChange, onRemove, t }) => {
  const isCustom = !row.isAssistant && row.specialization === 'CUSTOM';
  const selectedSpec = options.find(o => o.value === row.specialization);
  const selectedNoga = !isCustom && !row.isAssistant && selectedSpec?.nogaCode;
  const surchargeEligible = !row.isAssistant && !!selectedSpec?.nonIndicatedSurchargeEligible;

  // Candidate holding doctors: any OTHER personnel row that is not itself an
  // assistant and has a non-empty specialization (so the calc has a base rate
  // to halve).
  const holdingCandidates = personnel
    .map((p, i) => ({ ...p, index: i }))
    .filter(p => p.id !== row.id && !p.isAssistant && p.specialization && p.specialization !== 'CUSTOM');

  return (
    <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 10, padding: 12, marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
          {t('offers.rcpro.person', { n: idx + 1 })}
          {row.isAssistant && (
            <span style={{ marginLeft: 8, fontSize: 10, color: S.burgundy, fontWeight: 700 }}>
              · {t('offers.rcpro.assistantTag', { defaultValue: 'ASSISTANT' })}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: S.textSecondary, cursor: 'pointer' }}>
            <input
              type='checkbox'
              checked={!!row.isAssistant}
              onChange={e => onChange(row.id, 'isAssistant', e.target.checked)}
            />
            {t('offers.rcpro.isAssistant', { defaultValue: 'Médecin assistant' })}
          </label>
          <button
            onClick={() => onRemove(row.id)}
            disabled={isOnlyRow}
            title={isOnlyRow ? t('offers.rcpro.atLeastOnePerson') : t('offers.rcpro.delete')}
            style={{
              background: 'none', border: 'none', cursor: isOnlyRow ? 'not-allowed' : 'pointer',
              opacity: isOnlyRow ? 0.3 : 1, padding: 4,
            }}
          >
            <Icon name='trash' color={S.danger} size={16} />
          </button>
        </div>
      </div>

      {/* Who this line insures. Optional, and never a rating input -- the
          premium is driven by specialty and workload alone. A practice with
          several doctors needs each of them named on the proposal; a sole
          practitioner can leave it blank and the proposal omits the column. */}
      <Field label={t('offers.rcpro.personName')}>
        <input
          type='text'
          value={row.personName || ''}
          onChange={e => onChange(row.id, 'personName', e.target.value)}
          placeholder={t('offers.rcpro.personNamePlaceholder')}
          style={inputStyle}
        />
      </Field>

      {row.isAssistant ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px', gap: 10 }}>
          <Field label={t('offers.rcpro.holdingDoctor', { defaultValue: 'Médecin encadrant' })}>
            <select
              value={row.holdingRowId}
              onChange={e => onChange(row.id, 'holdingRowId', e.target.value)}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              <option value=''>{t('offers.common.selectPlaceholder')}</option>
              {holdingCandidates.map(h => (
                <option key={h.id} value={h.id}>
                  #{h.index + 1} · {options.find(o => o.value === h.specialization)?.label || h.specialization}
                </option>
              ))}
            </select>
            {holdingCandidates.length === 0 && (
              <div style={{ fontSize: 11, color: S.warning, marginTop: 4 }}>
                {t('offers.rcpro.needHoldingDoctor', { defaultValue: 'Add a non-assistant doctor row first.' })}
              </div>
            )}
            <div style={{ fontSize: 11, color: S.textMuted, marginTop: 4 }}>
              {t('offers.rcpro.assistantFormulaHint', { defaultValue: 'Premium = 50% of the holding doctor base rate × activity rate.' })}
            </div>
          </Field>
          <ActivityPercentField row={row} onChange={onChange} t={t} />
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px', gap: 10 }}>
            <Field label={t('offers.rcpro.specialization')}>
              <select
                value={row.specialization}
                onChange={e => onChange(row.id, 'specialization', e.target.value)}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value=''>{t('offers.common.selectPlaceholder')}</option>
                {options.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {selectedNoga && (
                <div style={{ fontSize: 11, color: S.textMuted, marginTop: 4 }}>
                  NOGA+ <span style={{ fontVariantNumeric: 'tabular-nums', color: S.textSecondary }}>{selectedNoga}</span>
                </div>
              )}
            </Field>
            <ActivityPercentField row={row} onChange={onChange} t={t} />
          </div>
          {isCustom && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: 10, marginTop: 4 }}>
              <Field label={t('offers.rcpro.customLabel')}>
                <input
                  value={row.customLabel}
                  onChange={e => onChange(row.id, 'customLabel', e.target.value)}
                  placeholder={t('offers.rcpro.customLabelPlaceholder')}
                  style={inputStyle}
                />
              </Field>
              <Field label={t('offers.rcpro.customRate')}>
                <input
                  type='number' step='0.01' min='0'
                  value={row.customRate}
                  onChange={e => onChange(row.id, 'customRate', e.target.value)}
                  style={inputStyle}
                />
              </Field>
            </div>
          )}
          {surchargeEligible && (
            <div style={{ marginTop: 6, padding: '8px 10px', background: 'rgba(245,158,11,0.08)', border: `1px solid ${S.warning}44`, borderRadius: 6 }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, color: S.textPrimary, cursor: 'pointer' }}>
                <input
                  type='checkbox'
                  checked={!!row.nonIndicatedOver10Pct}
                  onChange={e => onChange(row.id, 'nonIndicatedOver10Pct', e.target.checked)}
                />
                {t('offers.rcpro.nonIndicatedOver10Pct', { defaultValue: 'Non-indicated treatments exceed 10% of activity (adds surcharge)' })}
              </label>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const RcProOfferForm = ({ onClose, onCreated, reviseFrom = null, editDraft = null }) => {
  const { t } = useTranslation();
  const {
    rateTable, rateTableLoading, rateTableError,
    specializationOptions,
    policyholder, updatePolicyholder,
    clientId, selectClient, clearClient,
    brokerId, setBrokerId,
    productConfig, updateProductConfig,
    personnel, updatePersonnel, addPersonnel, removePersonnel,
    calc, calcLoading, calcError,
    canCalculate, missingFields,
    saving, saveError,
    saveDraft, createOffer,
    paymentTerms,
  } = useRcProOfferFunctions({ onClose, onCreated, reviseFrom, editDraft });

  // Tenant-driven discount capability. Hidden entirely when the tenant sets
  // features.rcProDiscount = false (ARGOS). The server independently forces the
  // discount to 0 for such tenants, so hiding the field is purely UX — a stale
  // client cannot reintroduce a discount. Default true (undefined => shown).
  const discountEnabled = useSelector(
    (s) => s?.tenantConfig?.config?.features?.rcProDiscount !== false,
  );


  if (rateTableLoading) {
    return <div style={{ padding: 40, fontSize: 12, color: S.textMuted }}>{t('offers.common.loadingRateTable')}</div>;
  }
  if (rateTableError) {
    return (
      <div style={{ padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, color: '#991b1b' }}>
        {rateTableError}
      </div>
    );
  }

  const sumOptions = rateTable?.availableSumsInsured || [];
  const showProvisional = calc?.isProvisional || rateTable?.isProvisional;

  return (
    <>
      {showProvisional && (
        <div style={{ padding: '10px 14px', background: 'rgba(245,158,11,0.10)', border: `1px solid ${S.warning}55`, borderRadius: 8, fontSize: 12, color: '#92400e', marginBottom: 10 }}>
          <Icon name='alert-triangle' color={S.warning} size={13} />{' '}
          <strong>{t('offers.rcpro.provisionalTitle', { defaultValue: 'PROVISIONAL rates' })}</strong> —{' '}
          {t('offers.rcpro.provisionalNote', { defaultValue: "pending Lloyd's underwriter approval." })}
        </div>
      )}

      <SectionTitle>{t('offers.common.policyholderData')}</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label={t('offers.common.client', { defaultValue: 'Client' })} span={2}>
          <ClientSelector value={clientId} onSelect={selectClient} onClear={clearClient} />
        </Field>
        <Field label={t('offers.common.broker', { defaultValue: 'Broker' })} span={2}>
          <BrokerSelect value={brokerId} onChange={setBrokerId} inputStyle={inputStyle} />
        </Field>
        <Field label={t('offers.common.companyName')} span={2}>
          <input value={policyholder.companyName} onChange={e => updatePolicyholder('companyName', e.target.value)} style={inputStyle} />
        </Field>
        <Field label={t('offers.common.address')} span={2}>
          <textarea
            value={policyholder.address}
            onChange={e => updatePolicyholder('address', e.target.value)}
            rows={2}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
          />
        </Field>
        <Field label={t('offers.common.canton')} span={2}>
          <select
            value={policyholder.canton}
            onChange={e => updatePolicyholder('canton', e.target.value)}
            style={{ ...inputStyle, cursor: 'pointer' }}
          >
            <option value=''>{t('offers.cantons.select')}</option>
            {CANTON_CODES.map(code => (
              <option key={code} value={code}>{t(`offers.cantons.${code}`)}</option>
            ))}
          </select>
        </Field>
        <Field label={t('offers.common.contactName')}>
          <input value={policyholder.contactName} onChange={e => updatePolicyholder('contactName', e.target.value)} style={inputStyle} />
        </Field>
        <Field label={t('offers.common.phone')}>
          <input value={policyholder.contactPhone} onChange={e => updatePolicyholder('contactPhone', e.target.value)} style={inputStyle} />
        </Field>
        <Field label={t('offers.common.email')} span={2}>
          <input type='email' value={policyholder.contactEmail} onChange={e => updatePolicyholder('contactEmail', e.target.value)} style={inputStyle} />
        </Field>
      </div>

      <SectionTitle>{t('offers.common.coverage')}</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
        <Field label={t('offers.rcpro.sumInsured')}>
          <select
            value={productConfig.sumInsured}
            onChange={e => updateProductConfig('sumInsured', e.target.value)}
            style={{ ...inputStyle, cursor: 'pointer' }}
          >
            {sumOptions.map(v => (
              <option key={v} value={v}>CHF {v.toLocaleString('de-CH')}</option>
            ))}
          </select>
        </Field>
      </div>

      <SectionTitle>{t('offers.rcpro.personnel')}</SectionTitle>
      {personnel.map((row, i) => (
        <PersonnelRow
          key={row.id}
          row={row}
          idx={i}
          options={specializationOptions}
          personnel={personnel}
          isOnlyRow={personnel.length === 1}
          onChange={updatePersonnel}
          onRemove={removePersonnel}
          t={t}
        />
      ))}
      <BtnSecondary onClick={addPersonnel} style={{ width: '100%', justifyContent: 'center', marginBottom: 8 }}>
        <Icon name='plus' color={S.textSecondary} size={14} /> {t('offers.rcpro.addPerson')}
      </BtnSecondary>

      <SectionTitle>{t('offers.common.commercialTerms')}</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: discountEnabled ? '1fr 1fr' : '1fr', gap: 12 }}>
        {discountEnabled && (
          <Field label={t('offers.rcpro.discountPct')}>
            <input
              type='number' step='0.1' min='0' max='100'
              value={productConfig.discountPercent}
              onChange={e => updateProductConfig('discountPercent', e.target.value)}
              style={inputStyle}
            />
          </Field>
        )}
        <Field label={t('offers.common.effectiveDate')}>
          <input
            type='date'
            value={productConfig.effectiveDate}
            onChange={e => updateProductConfig('effectiveDate', e.target.value)}
            style={inputStyle}
          />
        </Field>
        {/* Who the proposal is addressed to. Defaults to the insured; the
            broker only on request. An insured with no address on file falls
            back to the tenant's correspondence party in the renderer, so this
            can never print a blank block. */}
        <Field label={t('offers.common.correspondenceRecipient', 'Correspondence address')}>
          <select
            value={productConfig.correspondenceRecipient || 'insured'}
            onChange={e => updateProductConfig('correspondenceRecipient', e.target.value)}
            style={{ ...inputStyle, cursor: 'pointer' }}
          >
            <option value='insured'>{t('offers.common.recipientInsured', 'Insured')}</option>
            <option value='broker'>{t('offers.common.recipientBroker', 'Broker')}</option>
            <option value='tenant'>{t('offers.common.recipientTenant', 'Our office')}</option>
          </select>
        </Field>
        {/* Payment frequency — the shared control, driven only by the tenant's
            RC Pro paymentTerms. See PaymentFrequencyField. */}
        <Field label={t('offers.pgm.paymentFrequency')}>
          <PaymentFrequencyField
            terms={paymentTerms}
            premium={calc?.totalPremium}
            value={productConfig.paymentFrequency || paymentTerms.frequency}
            onChange={(v) => updateProductConfig('paymentFrequency', v)}
          />
        </Field>
      </div>

      <SectionTitle>{t('offers.common.premiumSummary')}</SectionTitle>
      {calcError && (
        <div style={{ padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, color: '#991b1b', marginBottom: 10 }}>
          {calcError}
        </div>
      )}
      {!canCalculate && !calc && (
        <div style={{ padding: 14, color: S.textMuted, fontSize: 12, fontStyle: 'italic' }}>
          {t('offers.rcpro.completeAllFields')}
          {missingFields && missingFields.length > 0 && (
            <div style={{ marginTop: 6, fontStyle: 'normal', fontSize: 11, color: S.textSecondary }}>
              {t('offers.rcpro.missingFieldsLabel', { defaultValue: 'Missing:' })}{' '}
              <span style={{ fontFamily: 'monospace' }}>{missingFields.join(', ')}</span>
            </div>
          )}
        </div>
      )}
      {canCalculate && calcLoading && !calc && (
        <div style={{ padding: 14, color: S.textMuted, fontSize: 12 }}>{t('offers.common.calculating')}</div>
      )}
      {calc && (
        <div style={{ background: 'rgba(128,0,32,0.03)', border: `1px solid ${S.burgundy}30`, borderRadius: 10, padding: 14 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 10 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${S.borderLight}` }}>
                <th style={{ textAlign: 'left', padding: '4px 6px', fontSize: 10, color: S.textMuted, fontWeight: 600, textTransform: 'uppercase' }}>{t('offers.rcpro.personnelColumn')}</th>
                <th style={{ textAlign: 'right', padding: '4px 6px', fontSize: 10, color: S.textMuted, fontWeight: 600, textTransform: 'uppercase' }}>{t('offers.rcpro.activityColumn')}</th>
                <th style={{ textAlign: 'right', padding: '4px 6px', fontSize: 10, color: S.textMuted, fontWeight: 600, textTransform: 'uppercase' }}>{t('offers.rcpro.rateColumn')}</th>
                <th style={{ textAlign: 'right', padding: '4px 6px', fontSize: 10, color: S.textMuted, fontWeight: 600, textTransform: 'uppercase' }}>{t('offers.rcpro.premiumColumn')}</th>
              </tr>
            </thead>
            <tbody>
              {calc.lineItems.map((li, i) => (
                <tr key={i}>
                  <td style={{ padding: '5px 6px', fontSize: 12, color: S.textPrimary }}>
                    {li.label}
                    {li.isCustom    && <span style={{ marginLeft: 6, fontSize: 10, color: S.warning, fontWeight: 600 }}>{t('offers.rcpro.customTag')}</span>}
                    {li.isAssistant && <span style={{ marginLeft: 6, fontSize: 10, color: S.burgundy, fontWeight: 600 }}>{t('offers.rcpro.assistantTag', { defaultValue: 'ASSISTANT' })}</span>}
                    {li.nogaCode && <span style={{ marginLeft: 6, fontSize: 10, color: S.textMuted }}>({li.nogaCode})</span>}
                  </td>
                  <td style={{ textAlign: 'right', padding: '5px 6px', fontSize: 12, fontVariantNumeric: 'tabular-nums', color: S.textSecondary }}>{fmtActivityPct(li.activityFTE)}</td>
                  <td style={{ textAlign: 'right', padding: '5px 6px', fontSize: 12, fontVariantNumeric: 'tabular-nums', color: S.textSecondary }}>{formatCHF(li.baseRate)}</td>
                  <td style={{ textAlign: 'right', padding: '5px 6px', fontSize: 12, fontVariantNumeric: 'tabular-nums', color: S.textPrimary, fontWeight: 600 }}>{formatCHF(li.linePremium)}</td>
                </tr>
              ))}
              {(calc.surchargeLines || []).map((sl, i) => (
                <tr key={`sc-${i}`} style={{ background: 'rgba(245,158,11,0.05)' }}>
                  <td style={{ padding: '5px 6px', fontSize: 12, color: S.textPrimary }}>
                    {sl.label}
                    <span style={{ marginLeft: 6, fontSize: 10, color: S.warning, fontWeight: 600 }}>
                      {t('offers.rcpro.surchargeTag', { defaultValue: 'SURCHARGE' })}
                    </span>
                    <span style={{ marginLeft: 6, fontSize: 10, color: S.textMuted }}>→ {sl.linkedLabel}</span>
                  </td>
                  <td style={{ textAlign: 'right', padding: '5px 6px', fontSize: 12, fontVariantNumeric: 'tabular-nums', color: S.textSecondary }}>{fmtActivityPct(sl.activityFTE)}</td>
                  <td style={{ textAlign: 'right', padding: '5px 6px', fontSize: 12, fontVariantNumeric: 'tabular-nums', color: S.textSecondary }}>{formatCHF(sl.baseRate)}</td>
                  <td style={{ textAlign: 'right', padding: '5px 6px', fontSize: 12, fontVariantNumeric: 'tabular-nums', color: S.textPrimary, fontWeight: 600 }}>{formatCHF(sl.linePremium)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ borderTop: `1px dashed ${S.borderLight}`, paddingTop: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: S.textPrimary, marginBottom: 4 }}>
              <span>{t('offers.rcpro.baseInsurance')}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatCHF(calc.basePremium)}</span>
            </div>
            {calc.surchargeTotal > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: S.textSecondary, marginBottom: 4 }}>
                <span>{t('offers.rcpro.surchargeSubtotal', { defaultValue: 'Non-indicated surcharge' })}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>+{formatCHF(calc.surchargeTotal)}</span>
              </div>
            )}
            {calc.discountAmount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: S.textSecondary, marginBottom: 4 }}>
                <span>{t('offers.rcpro.discount', { pct: Number(calc.inputs.discountPercent).toFixed(1) })}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>-{formatCHF(calc.discountAmount)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: S.textPrimary, marginBottom: 4 }}>
              <span>{t('offers.rcpro.subtotal')}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatCHF(calc.intermediatePremium)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: S.textSecondary, marginBottom: 4 }}>
              <span>{t('offers.rcpro.stampDuty', { pct: (Number(calc.stampDutyRate) * 100).toFixed(0) })}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatCHF(calc.stampDuty)}</span>
            </div>
            <div style={{ borderTop: `1px solid ${S.borderLight}`, marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8 }}>{t('offers.rcpro.totalAnnual')}</span>
              <span style={{ fontSize: 22, fontWeight: 800, color: S.burgundy, fontVariantNumeric: 'tabular-nums' }}>{formatCHF(calc.totalPremium)}</span>
            </div>
            {calc.isProvisional && (
              <div style={{ marginTop: 8, fontSize: 11, color: S.warning, fontStyle: 'italic', textAlign: 'right' }}>
                {t('offers.rcpro.provisionalShort', { defaultValue: '(provisional — pending Lloyd\'s approval)' })}
              </div>
            )}
          </div>
        </div>
      )}

      {saveError && (
        <div style={{ padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, color: '#991b1b', marginTop: 10 }}>
          {saveError}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
        <BtnSecondary onClick={onClose} style={{ flex: 1, justifyContent: 'center' }}>
          {t('offers.common.cancel')}
        </BtnSecondary>
        <BtnSecondary
          onClick={saveDraft}
          disabled={!calc || saving}
          style={{ flex: 1, justifyContent: 'center', opacity: (!calc || saving) ? 0.6 : 1 }}
        >
          {saving ? '…' : t('offers.common.saveDraft')}
        </BtnSecondary>
        <BtnPrimary
          onClick={createOffer}
          disabled={!calc || saving}
          style={{ flex: 1, justifyContent: 'center', opacity: (!calc || saving) ? 0.6 : 1 }}
        >
          <Icon name='check' color='#fff' size={15} /> {saving ? t('offers.common.creating') : t('offers.common.createOffer')}
        </BtnPrimary>
      </div>
    </>
  );
};

export default RcProOfferForm;
